
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import Multiselect from "vue-multiselect";
import { get } from "lodash";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { FeaturesList } from "@/store/types";
import { EventDetails, EventShareUser, ParkEventDTO } from "@/store/site-monitor/types";
import { UserPermissions } from "@/store/types";
import { AreaNote } from "@/store/area-notes/types";
import EventOutcomeDTO from "@/types/EventOutcomeDTO";

const SiteMonitor = namespace("siteMonitor");
const AreaNotes = namespace("areaNotes");

@Component({
	components: {
		multiselect: Multiselect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
	},
})
export default class EventLeave extends Vue {
	$refs!: {
		leaveModal: any;
	};

	@Getter("getFeaturesList") private featuresList: FeaturesList;
	@Getter("getPreviousRoute") private returnPath: string;
	@Getter("getPermissions") private permissions: UserPermissions;

	@SiteMonitor.Getter("getEventLeaveShown") private eventLeaveShown!: boolean;
	@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;
	@SiteMonitor.Getter("getEventShare") private eventShare!: EventShareUser[];
	@SiteMonitor.Getter("getUserID") private userId!: number;
	@SiteMonitor.Getter("getIsUploadingFile") private isUploadingFile: boolean;

	@SiteMonitor.Action private parkEvent: (request: ParkEventDTO) => Promise<void>;
	@SiteMonitor.Action private eventPassControl: ({ eventID, userID }) => Promise<void>;
	@SiteMonitor.Action private removeAdhocUsersFromEvent: () => void;
	@SiteMonitor.Action private loadParkedNoteOutcomesForEvent: (eventId: number) => Promise<EventOutcomeDTO[]>;

	@SiteMonitor.Mutation private setEventLeaveShown: (visible: boolean) => void;
	@SiteMonitor.Mutation private setEventDetails: (eventDetails: EventDetails) => void;

	@AreaNotes.Mutation private setAreaNotes: (areaNotes: AreaNote[]) => void;

	@Prop(Number) public eventid?: number;

	public otherOperator: any = null;
	public parkHours: number = 0;
	public parkMinutes: number = 10;
	public parkSeconds: number = 0;
	public parkNote: string = "";
	public parkNoteRequired: boolean = true;
	public permanentPark: boolean = false;

	private isParkEventChecked: boolean = false;
	private numberOfAlarms: number = 10;
	private priorityAlarm: number = 1000;
	private permanentParkHours: number = 0;
	private permanentParkMinutes: number = 10;
	private permanentTimeChecked: boolean = false;
	
	private predefinedParkNotes: EventOutcomeDTO[] = [];
	private predefinedNote: EventOutcomeDTO = null;

	public get sharedOperators() {
		return this.eventShare.filter(
			(user) => user.userID != null && user.userID != this.userId && !user.removed && user.isActive
		);
	}

	public get otherUserCount() {
		return this.sharedOperators == null ? 0 : this.sharedOperators.length;
	}

	public get canLeave() {
		if (this.otherOperator != null) return true;

		if (this.otherUserCount > 0) {
			return true;
		} else {
			if (this.parkNoteRequired) {
				return this.parkNote.length > 0;
			}
			return true;
		}
	}

	@Watch("eventLeaveShown")
	private async onEventRaiseShownChanged(value: boolean, oldValue: boolean) {
		if (this.$refs && this.$refs.leaveModal) {
			if (value) {	
				this.parkHours = this.eventDetails?.defaultParkForHours ?? 0;
				this.parkMinutes = this.eventDetails?.defaultParkForMinutes ?? 0;
				this.parkNoteRequired = this.eventDetails?.parkNoteRequired ?? true;
				
				if (this.isPredefinedParkedNotesEnabled) {
					this.predefinedParkNotes = await this.loadParkedNoteOutcomesForEvent(this.eventDetails.eventID);
				}

				this.$refs.leaveModal.show();
			} else {
				this.$refs.leaveModal.hide();
			}
		}
	}

	public async created() {
		this.setEventLeaveShown(false);
	}

	public async confirmLeave(): Promise<void> {
		if (this.canLeave) {
			if (this.otherOperator != null) {
				await this.eventPassControl({
					eventID: this.eventid,
					userID: this.otherOperator.userID,
				});
			} else {
				this.removeAdhocUsersFromEvent();
				if (this.parkNote.length > 500 || (this.parkNote.length == 0 && this.parkNoteRequired)) {
					return;
				}

				if (!this.isParkEventChecked && this.isParkingAnEventOptional) {
					this.parkHours = 0;
					this.parkMinutes = 0;
					this.parkSeconds = 0;
				}

				if (this.permanentPark && !this.permanentTimeChecked){
					this.parkHours = 0;
					this.parkMinutes = 0;
					this.parkSeconds = 0;
				}

				if (this.permanentPark && this.permanentTimeChecked) {
					this.parkHours = this.permanentParkHours;
					this.parkMinutes = this.permanentParkMinutes
				}

				await this.parkEvent({
					eventId: this.eventid,
					hours: this.parkHours,
					minutes: this.parkMinutes,
					seconds: this.parkSeconds,
					parkNote: this.parkNote,
					// Permanent park can only be set to true if the controls are shown by the permissions and feature checks
					permanentOptions: this.permanentPark && this.isParkEventChecked ? {
						permanentTimeSet: this.permanentTimeChecked,
						priority: this.priorityAlarm,
						numberOfAlarms: this.numberOfAlarms,
					} : null
				});
			}

			// ensure that the event details are cleared to prevent any interaction in the media matrix
			this.setEventDetails(null);
			this.setAreaNotes([]);

			// If this event was opened in a new window (and not from Views), close it
			if (window.opener !== null && this.returnPath != "/views") {
				window.close();
			} else {
				// send the user back to where they came from (if not set, return to event queue)
				let url = this.returnPath == "/" ? "/event-queue" : this.returnPath;
				this.$router.push({ path: url });
			}
		}
	}

	public cancel() {
		this.isParkEventChecked = false;
		this.setEventLeaveShown(false);
	}

	private validateHours() {
		if (this.parkHours < 0) {
			this.parkHours = 0;
		} else if (this.parkHours > 24) {
			this.parkHours = 24;
		}
	}

	private validateMinutes() {
		if (this.parkMinutes < 0) {
			this.parkMinutes = 0;
		} else if (this.parkMinutes > 59) {
			this.parkMinutes = 59;
		}
	}

	private get isParkingAnEventOptional(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "OptionalParkOnLeave"]);
	}

	private get isPermanentParkEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "PermanentPark"]);
	}

	private get isPredefinedParkedNotesEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "PredefinedParkedNotes"]);
	}

	public predefinedNoteChange(latestChoice: EventOutcomeDTO) {
		this.parkNote = latestChoice.title;
	}
}
